import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPhone, FiDelete, FiGlobe } from 'react-icons/fi';
import SEO from '../layout/SEO';

const DialPad = ({ onCall, walletBalance }) => {
    const [number, setNumber] = useState('');
    const [method] = useState('direct'); // Default to direct, bridge hidden for now
    const [rate] = useState(0.50);
    const [isCalling, setIsCalling] = useState(false); // NEW: Prevent duplicate clicks
    const navigate = useNavigate();

    const dialerSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Ringez Web Dialer",
        "applicationCategory": "CommunicationApplication",
        "operatingSystem": "Web Browser",
        "offers": {
            "@type": "Offer",
            "price": "0.50",
            "priceCurrency": "USD"
        }
    };

    const buttons = [
        { main: '1', sub: '' }, { main: '2', sub: 'ABC' }, { main: '3', sub: 'DEF' },
        { main: '4', sub: 'GHI' }, { main: '5', sub: 'JKL' }, { main: '6', sub: 'MNO' },
        { main: '7', sub: 'PQRS' }, { main: '8', sub: 'TUV' }, { main: '9', sub: 'WXYZ' },
        { main: '+', sub: 'intl' }, { main: '0', sub: 'empty' }, { main: '#', sub: 'empty' }
    ];

    const handlePress = (val) => {
        setNumber(prev => prev + val);
    };

    const handleBackspace = () => {
        setNumber(prev => prev.slice(0, -1));
    };

    const handleInputChange = (e) => {
        let val = e.target.value;
        // Auto-clean pasted numbers: allow only 0-9, +, *, #
        val = val.replace(/[^0-9+*#]/g, '');
        setNumber(val);
    };

    const clearNumber = () => setNumber('');

    return (
        <div className="dial-container animate-fade">
            <SEO
                title="Make International Calls from Your Browser"
                description="Use our web dialer to make secure international calls. No signup, no download required. Connect worldwide instantly."
                canonical="/call"
                schema={dialerSchema}
            />
            {/* Method Selector Hidden */}

            <div className="phone-display" style={{ position: 'relative', overflow: 'hidden' }}>
                <input
                    className="number-input"
                    value={number}
                    onChange={handleInputChange}
                    placeholder="+1 234 567 8900"
                    style={{
                        background: 'transparent', border: 'none', textAlign: 'center', width: '100%',
                        color: 'var(--text-main)',
                        fontSize: number.length > 12 ? '1.5rem' : number.length > 8 ? '2rem' : '2.5rem',
                        fontWeight: '700', outline: 'none', transition: 'font-size 0.2s'
                    }}
                />
                {number && (
                    <button
                        onClick={clearNumber}
                        style={{ position: 'absolute', right: '1rem', top: '1rem', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                    >
                        Clear
                    </button>
                )}
                {/* Rate badge removed */}
            </div>

            <div className="dial-grid">
                {buttons.map((btn, idx) => (
                    <button
                        key={idx}
                        className="dial-btn"
                        onClick={() => handlePress(btn.main)}
                        style={{ visibility: btn.sub === 'empty' && btn.main === '#' ? 'hidden' : 'visible' }}
                    >
                        {btn.main}
                        {btn.sub !== 'empty' && <span>{btn.sub}</span>}
                    </button>
                ))}

                <div style={{ gridColumn: 'span 3', display: 'flex', gap: '1rem' }}>
                    <button
                        className="call-btn"
                        style={{
                            flex: 3,
                            background: walletBalance < 1 ? 'var(--warning)' : 'var(--primary)',
                            opacity: isCalling ? 0.7 : 1,
                            cursor: isCalling ? 'not-allowed' : 'pointer'
                        }}
                        onClick={async () => {
                            if (isCalling) return; // Prevent duplicate clicks
                            if (walletBalance < 1) {
                                navigate('/wallet');
                            } else if (number) {
                                setIsCalling(true); // Disable button immediately
                                try {
                                    // Generate unique idempotency key
                                    const idempotencyKey = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                                    await onCall(number, method, idempotencyKey);
                                } finally {
                                    setIsCalling(false); // Re-enable after call completes
                                }
                            }
                        }}
                        disabled={!number || walletBalance < 1 || isCalling}
                    >
                        {isCalling ? (
                            <>
                                <span className="spinner" style={{ marginRight: '8px' }}>⏳</span>
                                Calling...
                            </>
                        ) : (
                            <>
                                <FiPhone size={24} />
                                {walletBalance < 1 ? 'Refill to Call' : 'Start Secure Call'}
                            </>
                        )}
                    </button>

                    <button
                        className="dial-btn"
                        style={{ flex: 1, height: '4rem', marginTop: '1rem' }}
                        onClick={handleBackspace}
                    >
                        <FiDelete size={24} />
                    </button>
                </div>
            </div>

            <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center', marginTop: '1rem' }}>
                Uses your browser microphone for direct, secure calling.
            </p>

            <p style={{
                color: 'var(--warning)',
                fontSize: '0.8rem',
                textAlign: 'center',
                marginTop: '0.75rem',
                padding: '0.5rem 1rem',
                background: 'rgba(245, 158, 11, 0.1)',
                borderRadius: '0.5rem',
                border: '1px solid rgba(245, 158, 11, 0.2)'
            }}>
                ⚠️ Include country code: +1 (USA), +91 (India), +44 (UK)
            </p>

            <div style={{ marginTop: '2.5rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '1.5rem' }}>
                <p style={{
                    textAlign: 'center', fontSize: '0.85rem', fontWeight: 600,
                    color: 'var(--text-main)', marginBottom: '1.5rem', opacity: 0.9
                }}>
                    No Login • No OTP • No Signup
                </p>

                <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem',
                    maxWidth: '360px', margin: '0 auto'
                }}>
                    {[
                        { label: 'Just Call', icon: '🟢' },
                        { label: 'Private', icon: '🔒' },
                        { label: 'Instant', icon: '⚡' },
                        { label: 'Pay As You Go', icon: '💳' }
                    ].map((item, i) => (
                        <div key={i} style={{
                            background: 'rgba(255,255,255,0.03)',
                            padding: '10px 12px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: 'var(--text-main)',
                            border: '1px solid rgba(255,255,255,0.05)'
                        }}>
                            <span>{item.icon}</span>
                            <span>{item.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default DialPad;
